import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { api, ApiError } from "../data/api/client";
import type { Me } from "../data/api/types";
import {
  authenticateWithPasskey,
  enrollWithPasskey,
} from "../data/api/webauthn";

export type SessionStatus = "anonymous" | "authenticated" | "loading";

export type SessionValue = {
  // Complete an enrollment link by minting a passkey (creates the account).
  readonly enroll: (token: string, displayName: string) => Promise<void>;
  // Set when the *initial* session probe could not reach the server (distinct
  // from a form-level auth failure, which is thrown to the caller).
  readonly error: string | null;
  readonly refresh: () => Promise<void>;
  readonly signInWithPasskey: () => Promise<void>;
  readonly signOut: () => Promise<void>;
  readonly status: SessionStatus;
  readonly user: Me | null;
};

const SessionContext = createContext<SessionValue | null>(null);

export const SessionProvider = ({
  children,
}: {
  readonly children: ReactNode;
}) => {
  const [user, setUser] = useState<Me | null>(null);
  const [status, setStatus] = useState<SessionStatus>("loading");
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const me = await api.get<Me>("/me");
      setUser(me);
      setStatus("authenticated");
      setError(null);
    } catch (err) {
      setUser(null);
      setStatus("anonymous");
      // A 401 is the normal "not signed in yet" case — not an error to show.
      if (err instanceof ApiError && !err.isUnauthenticated) {
        setError(err.message);
      } else {
        setError(null);
      }
    }
  }, []);

  useEffect(() => {
    // Probe the server for an existing session on mount. This is a legitimate
    // "synchronise with an external system" effect; the state updates happen
    // asynchronously inside refresh() after the fetch resolves, not
    // synchronously in the effect body.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refresh();
  }, [refresh]);

  const signInWithPasskey = useCallback(async (): Promise<void> => {
    // The verify call sets the session cookie server-side; refresh() then loads
    // the authenticated user.
    await authenticateWithPasskey();
    await refresh();
  }, [refresh]);

  const enroll = useCallback(
    async (token: string, displayName: string): Promise<void> => {
      // Registering the passkey mints the account + session; refresh() loads it.
      await enrollWithPasskey(token, displayName);
      await refresh();
    },
    [refresh],
  );

  const signOut = useCallback(async (): Promise<void> => {
    try {
      await api.del("/sessions/current");
    } catch {
      // Even if the revoke call fails, drop the local session state so the UI
      // returns to the sign-in screen.
    }
    setUser(null);
    setStatus("anonymous");
  }, []);

  const value = useMemo<SessionValue>(
    () => ({
      enroll,
      error,
      refresh,
      signInWithPasskey,
      signOut,
      status,
      user,
    }),
    [enroll, error, refresh, signInWithPasskey, signOut, status, user],
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
};

export const useSession = (): SessionValue => {
  const value = useContext(SessionContext);
  if (!value) {
    throw new Error("useSession must be used within a SessionProvider");
  }
  return value;
};
